import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useSiteImages, pick } from "@/lib/siteImages";

const collections = [
  {
    id: "petit",
    title: "Collezione Petit",
    description:
      "Collane personalizzabili con soggetti e incisioni: il nome dei tuoi bambini, sempre con te.",
    badge: "Più Richiesta",
    image:
      "https://images.unsplash.com/photo-1787084050199-0f399878a8a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxnb2xkJTIwbWVkYWxsaW9uJTIwcGVuZGFudCUyMG5lY2tsYWNlfGVufDB8fHx8MTc5MTgxNjM0N3ww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "legami",
    title: "Collezione Legami",
    description:
      "Bracciali componibili con le lettere: componi il nome o la parola che ti lega a chi ami.",
    badge: "Componibile",
    image:
      "https://images.unsplash.com/photo-1786118791301-bede74b9c3b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY6NjV8MHwxfHNlYXJjaHwzfHxsZXR0ZXIlMjBiZWFkcyUyMGJyYWNlbGV0JTIwd3Jpc3R8ZW58MHx8fHwxNzkxODE2MzUxfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "my-letters",
    title: "Collezione My Letters",
    description:
      "Iniziali preziose in argento: una lettera, un ricordo, un legame indissolubile.",
    badge: "Essenziale",
    image:
      "https://images.unsplash.com/photo-1784751722900-c5f70f8b667e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxpbml0aWFsJTIwbGV0dGVyJTIwcGVuZGFudCUyMG5lY2tsYWNlfGVufDB8fHx8MTc5MTgxNjM0MHww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "bon-bon",
    title: "Collezione Bon Bon",
    description:
      "Collane con perle personalizzabili: scegli catena, colore delle perle, soggetti e incisione.",
    badge: "Personalizzabile",
    image:
      "https://images.unsplash.com/photo-1613315986155-820432757035?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwcGVhcmwlMjBuZWNrbGFjZSUyMHdvcm58ZW58MHx8fHwxNzkxODE2MzQwfDA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "moments",
    title: "Collezione Moments",
    description:
      "Solo pendente: il soggetto che ami, personalizzato con la tua incisione.",
    badge: "Solo pendente",
    image:
      "https://images.unsplash.com/photo-1702476320482-0736c4b962f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxsb29zZSUyMGpld2VscnklMjBjaGFybXMlMjBjb2xsZWN0aW9uJTIwZmxhdCUyMGxheXxlbnwwfHx8fDE3OTE4MTYzNDB8MA&ixlib=rb-4.1.0&q=85",
  },
];

export default function Collections() {
  const siteImgs = useSiteImages();
  const handleSelect = (id) => {
    window.dispatchEvent(new CustomEvent("mavi:select-collection", { detail: id }));
    document.querySelector("#configura")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="collezioni" className="py-28 lg:py-36 bg-[#FDF8F7]" data-testid="collections-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-16 lg:mb-20 max-w-2xl"
        >
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-[#C4758A]" data-testid="collections-overline">
            Le Collezioni
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#37282B] leading-tight" data-testid="collections-headline">
            Ogni pezzo nasce da una <span className="italic text-[#E2A3B1]">storia d'amore</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#6B5459] leading-relaxed" data-testid="collections-subtitle">
            Scegli la tua collezione e personalizzala: un nome inciso, un ricordo indissolubile.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {collections.map((c, i) => (
            <motion.article
              key={c.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.12 }}
              className={`group cursor-pointer ${i % 2 === 1 ? "lg:mt-12" : ""}`}
              onClick={() => handleSelect(c.id)}
              data-testid={`collection-card-${c.id}`}
            >
              <div className="relative overflow-hidden rounded-2xl aspect-[3/4] bg-[#FBECEA]">
                <img
                  src={pick(siteImgs, `collezione_${c.id}`, c.image)}
                  alt={c.title}
                  className="w-full h-full object-contain transition-transform duration-700 ease-out group-hover:scale-105"
                  data-testid={`collection-image-${c.id}`}
                />
                <div className="absolute inset-0 bg-[#37282B]/0 group-hover:bg-[#37282B]/25 transition-colors duration-500" />
                <span
                  className="absolute top-4 left-4 bg-[#FDF8F7]/95 text-[#37282B] text-[10px] uppercase tracking-[0.18em] font-semibold px-3 py-1.5 rounded-full"
                  data-testid={`collection-badge-${c.id}`}
                >
                  {c.badge}
                </span>
                <div className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-[#E2A3B1] text-[#37282B] flex items-center justify-center opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-400">
                  <ArrowUpRight className="w-4 h-4" />
                </div>
              </div>
              <div className="pt-5">
                <h3 className="font-serif-display text-xl sm:text-2xl text-[#37282B] group-hover:text-[#C4758A] transition-colors duration-300" data-testid={`collection-title-${c.id}`}>
                  {c.title}
                </h3>
                <p className="mt-2 text-sm text-[#6B5459] leading-relaxed">{c.description}</p>
                <span className="inline-block mt-3 text-xs uppercase tracking-[0.18em] font-semibold text-[#C4758A] group-hover:underline underline-offset-4">
                  Personalizza ora
                </span>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
